import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMcfConnexionAlerte, type McfConnexionAlerte } from '../../api/queries/mcf';
import { usePermissions } from '../../billing-host/permissions';
import { useAuth } from '../../billing-host/session';
import { t, type TranslationKey } from '../../lib/billing-i18n';

/**
 * SFE §2.31 and MCF annex C2h — informs the user, daily and through the user
 * interface, when the MCF machine attached to the SFE has not reached the
 * Administration for more than the API's threshold (`seuilJours`, one day).
 * When the MCF sits behind a site agent and its date could not be read, a
 * distinct warning says so and what the operator can do. A third warning
 * relays annex status bit 1.4 (daily report due) as the MCF last reported
 * it. Acknowledging stores the calendar day, per warning, so a reload does
 * not repeat it until the next day.
 */

const STORAGE_PREFIX = 'efacture.mcf.alerte-connexion.vue';

/** Suffix of the acknowledgement key of the read warning. */
const READ_WARNING_SUFFIX = ':lecture';

/** Suffix of the acknowledgement key of the daily-report warning. */
const DAILY_REPORT_SUFFIX = ':rapport-quotidien';

/**
 * Annex SECEF BF ST 1.0, status bit 1.4 (p. 24). The annex's command list
 * (§4.1, p. 7) has no daily-report command, so the SFE cannot produce it.
 */
const DAILY_REPORT_MESSAGE =
  'le MCF indique que le rapport quotidien doit être fait. Le protocole MCF-SFE ne prévoit aucune ' +
  'commande permettant au logiciel de le produire : contactez votre installateur agréé.';

type ReadCause = NonNullable<McfConnexionAlerte['causeLectureIndisponible']>;

/** What the operator can do about each cause of an unread MCF. */
const READ_WARNING_ACTIONS: Record<ReadCause, TranslationKey> = {
  AGENT_DECONNECTE: 'connexionBanner.read.AGENT_DECONNECTE',
  AGENT_NON_VERIFIE: 'connexionBanner.read.AGENT_NON_VERIFIE',
  LECTURE_MCF_ECHOUEE: 'connexionBanner.read.LECTURE_MCF_ECHOUEE',
  SIGNATURE_INTERROMPUE: 'connexionBanner.read.SIGNATURE_INTERROMPUE',
  AGENT_PROTOCOLE_LEGACY: 'connexionBanner.read.AGENT_PROTOCOLE_LEGACY',
};

/** The arbitration screen of the signatures in doubt, and the permission its route requires. */
const ARBITRATION_PATH = '/invoices/signatures-en-doute';
const ARBITRATION_PERMISSION = 'mcf:arbitrer-signature';

/**
 * Acknowledgement key of one operator on one dossier: a shared POS
 * workstation must not carry a cashier's dismissal to the next.
 */
function storageKey(user: { id: string; tenantId: string } | null): string {
  return user ? `${STORAGE_PREFIX}:${user.tenantId}:${user.id}` : STORAGE_PREFIX;
}

/** Local calendar day, the unit «quotidiennement» counts in. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function readAcknowledgedDay(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Storage refused (private window, blocked site data): warn again.
    return null;
  }
}

interface Warning {
  readonly suffix: string;
  readonly message: string;
  /** Set when the recovery is taken on the arbitration screen. */
  readonly arbitration?: true;
}

/** The §2.31 warning to show, with its acknowledgement key suffix; null when none. */
function connexionWarning(alerte: McfConnexionAlerte | null | undefined): Warning | null {
  if (!alerte) return null;
  // An unread MCF is shown only with a cause (a site agent's read), which
  // the operator can act on; the API logs the other unread states.
  const readCause = alerte.lectureIndisponible ? alerte.causeLectureIndisponible : null;
  if (!alerte.avertissementRequis && readCause === null) return null;
  return readCause === null
    ? {
        suffix: '',
        message:
          `plus de ${alerte.seuilJours * 24} heures sans connexion au serveur de l'Administration. ` +
          `Contactez votre installateur agréé.`,
      }
    : {
        suffix: READ_WARNING_SUFFIX,
        message:
          t('connexionBanner.read.prefix') +
          t(READ_WARNING_ACTIONS[readCause]) +
          (readCause === 'SIGNATURE_INTERROMPUE' && alerte.repriseApresFactureCertifiee === true
            ? t('connexionBanner.read.SIGNATURE_INTERROMPUE.reprise')
            : ''),
        ...(readCause === 'SIGNATURE_INTERROMPUE' ? { arbitration: true as const } : {}),
      };
}

export function McfConnexionBanner() {
  const { data } = useMcfConnexionAlerte();
  const { user } = useAuth();
  const canArbitrate = usePermissions().hasPermission(ARBITRATION_PERMISSION);
  // Acknowledged day per key, so a tenant or operator switch reads that
  // session's own acknowledgement instead of inheriting this one.
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({});

  // Every warning names the MCF: none unless the API says the caller's
  // établissement needs one today.
  const alertes = data?.mcfRequis === true ? data : undefined;
  const warnings = [
    connexionWarning(alertes?.alerteConnexion),
    alertes?.alerteRapportQuotidien ? { suffix: DAILY_REPORT_SUFFIX, message: DAILY_REPORT_MESSAGE } : null,
  ]
    .filter((warning): warning is Warning => warning !== null)
    .map((warning) => ({ ...warning, key: storageKey(user) + warning.suffix }))
    .filter(({ key }) => (acknowledged[key] ?? readAcknowledgedDay(key)) !== today());
  if (warnings.length === 0) return null;

  const acknowledge = (key: string) => {
    const day = today();
    try {
      window.localStorage.setItem(key, day);
    } catch {
      // Storage refused: the warning returns on the next mount, by design.
    }
    setAcknowledged((previous) => ({ ...previous, [key]: day }));
  };

  return (
    <>
      {warnings.map(({ key, message, arbitration }) => (
        <div
          key={key}
          role="alert"
          className="mb-4 flex flex-wrap items-start justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
        >
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <span>
              <span className="font-semibold">Module de contrôle de facturation (MCF)</span>{' '}
              — {message}
              {arbitration && canArbitrate ? (
                <>
                  {' '}
                  <Link to={ARBITRATION_PATH} className="font-medium underline">
                    {t('connexionBanner.read.arbitrationLink')}
                  </Link>
                </>
              ) : null}
            </span>
          </div>
          <button
            type="button"
            onClick={() => acknowledge(key)}
            aria-label="Masquer l'avertissement pour aujourd'hui"
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-amber-900 hover:bg-amber-100"
          >
            <X className="h-4 w-4" />
            J'ai compris
          </button>
        </div>
      ))}
    </>
  );
}

export default McfConnexionBanner;
